import * as React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";

import type { RequestBlocker } from "@/hooks/api/use-campaign-requests";

import { REQUEST_BLOCKER_COLORS, REQUEST_BLOCKER_HELP, REQUEST_BLOCKER_LABELS } from "../constants";
import { labelOf } from "../humanise";
import { HelpTooltip } from "./help-tooltip";

export interface BlockerChipsProps {
	blockers: RequestBlocker[];
	size?: "small" | "medium";
	/**
	 * What to draw when nothing is outstanding. The client list leaves the cell blank on purpose —
	 * "blank when it's on us and there's nothing they can do" — so this defaults to a dash and is
	 * given a "None" chip only where the absence is itself the answer.
	 */
	emptyContent?: React.ReactNode;
	/**
	 * Put the reason on the page instead of behind a hover.
	 *
	 * Off in table cells, where a row is one line high and there is physically nowhere to put a
	 * sentence. On wherever the screen has the room — a detail panel, a step — because a client
	 * who has to hover a chip to find out what is being asked of them has already been failed
	 * once.
	 */
	explain?: boolean;
}

/**
 * What is outstanding on a request.
 *
 * Amber, never red: a blocker does not stop a request being quoted or confirmed, and only the
 * move to `ready` requires the ones that apply to be cleared. Each chip carries the reason,
 * because "PO" on its own tells a client nothing about what to do next.
 */
export function BlockerChips({
	blockers,
	size = "small",
	emptyContent = "—",
	explain = false,
}: BlockerChipsProps): React.JSX.Element {
	if (blockers.length === 0) return <React.Fragment>{emptyContent}</React.Fragment>;

	if (explain) {
		return (
			<Stack spacing={1}>
				{blockers.map((blocker) => (
					<Stack key={blocker} spacing={0.5} sx={{ alignItems: "flex-start" }}>
						<Chip
							color={REQUEST_BLOCKER_COLORS[blocker] ?? "warning"}
							label={labelOf(REQUEST_BLOCKER_LABELS, blocker)}
							size={size}
							variant="soft"
						/>
						{REQUEST_BLOCKER_HELP[blocker] ? (
							<Typography variant="caption" color="text.secondary">
								{REQUEST_BLOCKER_HELP[blocker]}
							</Typography>
						) : null}
					</Stack>
				))}
			</Stack>
		);
	}

	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
			{blockers.map((blocker) => (
				<HelpTooltip key={blocker} title={REQUEST_BLOCKER_HELP[blocker]} describeChild={false}>
					<Chip
						color={REQUEST_BLOCKER_COLORS[blocker] ?? "warning"}
						label={labelOf(REQUEST_BLOCKER_LABELS, blocker)}
						size={size}
						variant="soft"
					/>
				</HelpTooltip>
			))}
		</Box>
	);
}
